import React, { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from "@mui/material";
import { getUserById, setTeacherSalary, updateUser } from "../../services/dataService";
import { logUserActivity } from "../../utils/activityLogger";
import { parseMoneyInput, verifyChip } from "../../utils/tutorPresentation";

const subjectsText = (subjects) => (Array.isArray(subjects) ? subjects.join(", ") : subjects || "");

// Name · class link · subjects · salary, with the verify status shown but not edited. Moved out
// of the Tutors list (047 step 4) so the list and the tutor page share one dialog; the save is
// the list's, unchanged.
const EditTutorDialog = ({ open, onClose, tutor, actingUser, onSaved }) => {
  const [form, setForm] = useState({ name: "", classLink: "", subjects: "", salary: 0, fixedMonthlyPay: "" });
  const [saving, setSaving] = useState(false);
  // Save failures stay inside the dialog, with the entered values.
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setForm({
        name: tutor?.name || "",
        classLink: tutor?.classLink || tutor?.class_link || "",
        subjects: subjectsText(tutor?.subjects),
        salary: Number(tutor?.salary) > 0 ? String(tutor.salary) : "",
        fixedMonthlyPay: Number(tutor?.fixedMonthlyPay) > 0 ? String(tutor.fixedMonthlyPay) : "",
      });
      setError("");
    }
  }, [open, tutor]);

  const save = async () => {
    if (!tutor?.id) return;
    const id = tutor.id;
    const { name, classLink, subjects, salary, fixedMonthlyPay } = form;
    try {
      setSaving(true);
      setError("");

      const salaryIn = parseMoneyInput(salary, { label: "Salary" });
      const fixedIn = parseMoneyInput(fixedMonthlyPay, { label: "Fixed monthly pay", max: 100000 });
      if (salaryIn.error || fixedIn.error) {
        setError(salaryIn.error || fixedIn.error);
        return;
      }
      const nextSalary = salaryIn.value;
      const nextFixed = fixedIn.value;
      // Changed = differs from what the dialog opened with, so a save that did not touch a field
      // never writes the list's older copy over a newer value another admin saved.
      const salaryChanged = nextSalary !== (Number(tutor?.salary) || 0);
      const fixedChanged = nextFixed !== (Number(tutor?.fixedMonthlyPay) || 0);

      // Get the current/old data before updating
      const oldData = await getUserById(id);
      // Without the tutor's saved values the callable would send salary 0 for a rate nobody changed.
      if (!oldData) {
        setError("Could not load this tutor. Nothing has been changed.");
        return;
      }
      const before = {
        name: oldData.name || "",
        subjects: subjectsText(oldData.subjects),
        classLink: oldData.classLink || oldData.class_link || "",
        salary: oldData.salary || 0,
        fixedMonthlyPay: Number(oldData.fixedMonthlyPay) || 0,
      };

      // verifyStatus is deliberately absent (spec 026 V2 Step 3): the
      // Verification queue is now the only writer of that field, so an
      // unrelated edit here (e.g. just the class link) must never carry a
      // stale verifyStatus back onto the doc.
      //
      // salary left with it (spec 026 D21): it is in firestore.rules'
      // serverOnlyFields(), which now guards the isAdmin() branch too, so this
      // payload would be refused *entirely* — profile fields included — if the
      // rate rode along in it. `setTeacherSalary` is the only writer now.
      const payload = {
        name,
        subjects: subjects
          .split(",")
          .map((s) => s.trim())
          .filter((s) => s.length > 0),
        classLink,
        class_link: classLink,
      };

      // The rate and fixed pay go first, and only when either actually moved. First
      // because it is the call that can be refused on its merits (not a teacher, not
      // a number, not an admin) — refused here, nothing at all has been written and
      // the dialog stays open on the error. Residual, accepted: if the profile write
      // below fails after this one landed, the new rate is live with no activity
      // row describing it. The reverse order would instead leave a logged profile
      // edit claiming a rate that never landed, which reads as true and is worse.
      // 053 D8: one call carries both; the server stamps when fixed pay starts (R12).
      if (salaryChanged || fixedChanged) {
        await setTeacherSalary({
          userId: id,
          salary: salaryChanged ? nextSalary : (before.salary ?? 0),
          fixedMonthlyPay: fixedChanged ? nextFixed : undefined,
        });
      }

      await updateUser(id, payload);

      // Log the activity with before/after comparison
      await logUserActivity({
        userId: actingUser?.uid || actingUser?.id || "unknown",
        userName: actingUser?.name || actingUser?.displayName || "Unknown User",
        userEmail: actingUser?.email || "unknown@example.com",
        action: "UPDATE_TUTOR",
        entityType: "tutor",
        entityId: id,
        entityName: name || "Unknown Tutor",
        before,
        after: {
          name,
          subjects: payload.subjects.join(", "),
          classLink,
          // What actually landed: the rate and fixed pay only moved if the callable above ran.
          salary: salaryChanged ? nextSalary : (before.salary ?? 0),
          fixedMonthlyPay: fixedChanged ? nextFixed : (before.fixedMonthlyPay ?? 0),
        },
        description: `${actingUser?.name || "User"} updated tutor ${name || id}`,
      });

      onClose();
      onSaved();
    } catch (e) {
      // `message` is the callable's own HttpsError text ("Only a teacher has a salary",
      // "salary must be a number of zero or more") — the reason the save was refused,
      // which is the only thing that tells the admin what to change.
      setError(e?.message || "Could not save this tutor. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const fieldSx = { borderRadius: "8px", "& .MuiInputBase-root": { borderRadius: "6px" } };
  const buttonSx = { padding: "8px 32px", fontSize: 15, fontWeight: 500 };
  const status = verifyChip(tutor);

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: "8px" } }}>
      <DialogTitle style={{ fontWeight: 600 }}>Edit Tutor</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <TextField label="Name" fullWidth margin="normal" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} sx={fieldSx} />
        <TextField
          label="Class Link"
          fullWidth
          margin="normal"
          value={form.classLink}
          onChange={(e) => setForm({ ...form, classLink: e.target.value })}
          sx={fieldSx}
        />
        <TextField
          label="Subjects (comma-separated)"
          fullWidth
          margin="normal"
          value={form.subjects}
          onChange={(e) => setForm({ ...form, subjects: e.target.value })}
          sx={fieldSx}
        />
        <Box sx={{ mt: 2, mb: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
            Verify Status
          </Typography>
          {/* Read-only (spec 026 V2 Step 3): the Verification queue is the only writer of verifyStatus. */}
          <Chip
            size="small"
            component={RouterLink}
            to="/verification"
            clickable
            label={status.label}
            color={status.color}
            sx={{ borderRadius: "6px", fontSize: 14, fontWeight: 500 }}
          />
        </Box>
        <TextField
          type="text"
          label="Salary"
          fullWidth
          margin="normal"
          value={form.salary}
          onChange={(e) => setForm({ ...form, salary: e.target.value })}
          inputProps={{ inputMode: "decimal" }}
        />
        <TextField
          type="text"
          label="Fixed monthly pay (E£)"
          fullWidth
          margin="normal"
          value={form.fixedMonthlyPay}
          onChange={(e) => setForm({ ...form, fixedMonthlyPay: e.target.value })}
          helperText="Added to Owed in full when each pay cycle ends. Leave empty for none."
          inputProps={{ inputMode: "decimal" }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving} sx={buttonSx}>
          Cancel
        </Button>
        <Button onClick={save} variant="contained" disabled={saving} sx={buttonSx}>
          {saving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditTutorDialog;
